/* eslint-disable no-nested-ternary */

import keyCodes from 'keycodes'
import * as React from 'react'
import type {SyntheticEvent, KeyboardEvent} from 'react'

import {handleNavigationClick} from '@base/app-services/navigation'
import {autobind} from '@base/decorators'
import {themeable, pickProps, classNames} from '@base/react-utils'
import isExternalLink from '@base/url-utils/isExternalLink'
import sanitizeUrl from '@base/url-utils/sanitizeUrl'

import baseTheme from './Link.css'

export type onClickType = (
  event: SyntheticEvent | KeyboardEvent
) => void | Q.Promise<void> | Promise<void>

export type LinkProps = {
  alt?: string
  color?: 'default' | 'inherit' | 'invert'
  id?: string
  href?: string
  target?: string
  title?: string
  rel?: string
  disabled?: boolean
  children?: React.ReactNode | null
  className?: string
  theme?: any
  tabIndex?: number
  autoFocus?: boolean
  onClick?: onClickType | null
  onKeyDown?: ((event: KeyboardEvent) => void) | null
  // If dontLink is true, a <span/> is rendered instead of an <a/>. This is only
  // useful if your components end up having nested links. It lets you create an
  // element that is styled like a link, inside of a bigger linked area.
  dontLink?: boolean
}

const autoFocusRef = (node) => node && node.focus()

// For security reasons links with target=_blank and those leading to external sites
// should include rel=noopener.
// More information about this issue under: https://mathiasbynens.github.io/rel-noopener/
export const getRelForLink = ({
  rel = '',
  href,
  target,
}: {
  rel?: string
  href?: string
  target?: string
}) => {
  if (!href || rel.includes('noopener')) return rel
  const isNoopenerNeeded = target === '_blank' || isExternalLink(href)
  if (isNoopenerNeeded) return `${rel} noopener`.trim()
  return rel
}

@themeable(baseTheme)
class Link extends React.Component<LinkProps> {
  // from @themeable
  theme: any

  static defaultProps: Partial<LinkProps> = {
    color: 'default',
    tabIndex: 0,
  }

  @autobind
  handleClick(event: SyntheticEvent) {
    if (event.defaultPrevented) return
    const {onClick, disabled} = this.props
    if (disabled !== true && onClick) {
      onClick(event)
    }
    if (event.defaultPrevented) {
      return
    }
    if (this.props.disabled !== true && this.props.href) {
      handleNavigationClick(this.props.href, this.props.target, event)
    }
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (event.defaultPrevented) return
    const {onClick, onKeyDown, disabled} = this.props
    if (disabled !== true && onClick && event.keyCode === keyCodes('enter')) {
      event.preventDefault()
      onClick(event)
    }
    if (onKeyDown) onKeyDown(event)
  }

  getClassName() {
    const {color, disabled, className} = this.props
    const theme = this.theme
    return classNames([
      theme.Link,
      theme[`color_${color || 'default'}`],
      disabled && theme.disabled,
      className,
    ])
  }

  render() {
    const {children, autoFocus, tabIndex, href, target, rel} = this.props
    const Component = this.props.dontLink ? 'span' : 'a'
    return (
      <Component
        ref={autoFocus ? autoFocusRef : null}
        tabIndex={autoFocus && tabIndex === undefined ? 0 : tabIndex}
        href={sanitizeUrl(href)}
        target={target}
        // forward style, data-* and all events
        {...pickProps(this.props, [
          'style',
          'id',
          /^data-/,
          /^on[A-Z]/,
          'href',
          'target',
          'title',
          'rel',
        ])}
        rel={getRelForLink({rel, href, target})}
        onClick={this.handleClick}
        onKeyDown={this.handleKeyDown}
        className={this.getClassName()}
      >
        {children}
      </Component>
    )
  }
}

export default Link
